import { differenceInMinutes, parse } from 'date-fns';
import { equals } from 'ramda';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import useSelectedStudent from 'src/hooks/useSelectedStudent';
import { useStudentHasServiceType } from 'src/hooks/useStudentHasServiceType';
import { isParaServiceType } from 'src/logic/serviceType';
import { invalidateSingleStudentTags } from 'src/pages/single-student/singleStudentApi';
import { invalidateStudentsListTags } from 'src/pages/students/studentsApi';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';
import { apiErrorsToDict } from 'src/types/api-error-response';

import {
  useCancelSessionMutation,
  useConfirmSessionMutation,
  useCreateRecordSessionMutation,
  useDeleteSessionMutation,
  useLazyConfirmAvailabilityHoursQuery,
  useLazyGetAvailableHoursQuery,
  useRescheduleSessionMutation,
} from '../scheduleApi';
import { EditionForm, FormData, FormOptions, RecordSessionContext } from './RecordSessionContext';

type UseRecordSessionProps = {
  onSuccessCb?: () => void;
  isPara?: boolean;
};

export const useRecordSession = ({ onSuccessCb }: UseRecordSessionProps) => {
  const appDispatch = useAppDispatch();
  const { state, dispatch, features } = useContext(RecordSessionContext);

  const [
    createRecordSession,
    {
      isSuccess: isCreateSuccess,
      error: createError,
      isLoading: isLoadingCreate,
      reset: resetCreate,
    },
  ] = useCreateRecordSessionMutation();
  const [
    confirmRecordSession,
    {
      isSuccess: isConfirmSuccess,
      error: confirmError,
      isLoading: isLoadingConfirm,
      reset: resetConfirm,
    },
  ] = useConfirmSessionMutation();
  const [
    rescheduleRecordSession,
    {
      isSuccess: isRescheduleSuccess,
      error: rescheduleError,
      isLoading: isLoadingReschedule,
      reset: resetReschedule,
    },
  ] = useRescheduleSessionMutation();
  const [
    deleteRecordSession,
    { isSuccess: isDeleteSuccess, error: deleteError, reset: resetDelete },
  ] = useDeleteSessionMutation();
  const [
    cancelRecordSession,
    { isSuccess: isCancelSuccess, error: cancelError, reset: resetCancel },
  ] = useCancelSessionMutation();

  const isEdit = state.isInitialized && 'parentEvent' in state && state.parentEvent;

  const resetAllMutations = useCallback(() => {
    resetCreate();
    resetConfirm();
    resetReschedule();
    resetDelete();
    resetCancel();
  }, [resetCreate, resetConfirm, resetReschedule, resetDelete, resetCancel]);

  const existingStudent = 'existingSession' in state ? state.existingSession?.student : undefined;
  const { selectedStudent } = useSelectedStudent(true, state?.formData?.studentId, existingStudent);
  const isPara =
    selectedStudent?.isPara === true ||
    isParaServiceType(selectedStudent?.serviceType) ||
    isParaServiceType(existingStudent?.serviceType);
  const hasSeitServiceType = useStudentHasServiceType(state?.formData?.studentId, 'SEIT');

  const [
    confirmSelectedAvailability,
    {
      currentData: confirmationResponse,
      error: confirmationError,
      originalArgs: confirmationRequest,
    },
  ] = useLazyConfirmAvailabilityHoursQuery();

  const requiredGoals = useMemo(() => {
    if (confirmationResponse?.canSchedule && confirmationResponse.requiredGoals !== undefined) {
      return confirmationResponse.requiredGoals;
    }

    if (hasSeitServiceType && state.formData?.startTime && state.formData?.finishTime) {
      const baseDate = new Date(1970, 0, 1);
      const start = parse(state.formData.startTime, 'HH:mm', baseDate);
      const finish = parse(state.formData.finishTime, 'HH:mm', baseDate);
      const durationInMinutes = differenceInMinutes(finish, start);
      return Math.ceil(durationInMinutes / 30);
    }
    return 0;
  }, [
    state.formData?.startTime,
    state.formData?.finishTime,
    hasSeitServiceType,
    confirmationResponse,
  ]);

  const hasSuccess =
    (isCreateSuccess && !createError) ||
    (isConfirmSuccess && !confirmError) ||
    (isDeleteSuccess && !deleteError) ||
    (isCancelSuccess && !cancelError) ||
    (isRescheduleSuccess && !rescheduleError);

  useEffect(() => {
    if (!hasSuccess) return;

    dispatch({ type: 'reset' });

    const actionLabel =
      isCreateSuccess && !createError
        ? 'recorded'
        : isConfirmSuccess && !confirmError
        ? 'confirmed'
        : isDeleteSuccess && !deleteError
        ? 'deleted'
        : isCancelSuccess && !cancelError
        ? 'cancelled'
        : isRescheduleSuccess && !rescheduleError
        ? 'rescheduled'
        : '';

    if (!actionLabel) return;

    const toastText = `Session ${actionLabel}.`;

    appDispatch(
      addToast({
        type: 'positive',
        text: toastText,
      }),
    );
    appDispatch(invalidateSingleStudentTags(['StudentEvents']));
    appDispatch(invalidateStudentsListTags(['StudentsList']));

    onSuccessCb?.();
  }, [
    hasSuccess,
    isConfirmSuccess,
    isCreateSuccess,
    isDeleteSuccess,
    isCancelSuccess,
    isRescheduleSuccess,
    createError,
    confirmError,
    deleteError,
    cancelError,
    rescheduleError,
    appDispatch,
    dispatch,
    onSuccessCb,
  ]);

  const [
    getAvailability,
    {
      currentData: availability,
      error: availabilityError,
      originalArgs: availabilityArgs,
      isFetching: isFetchingAvailability,
    },
  ] = useLazyGetAvailableHoursQuery();

  useEffect(() => {
    const { formData } = state;
    if (formData && formData.studentId && formData.date) {
      const args = {
        studentId: formData.studentId,
        date: formData.date,
      };

      if (
        !equals(args, availabilityArgs) ||
        (!availability && !isFetchingAvailability && !availabilityError)
      ) {
        getAvailability(args);
      }
    }
  }, [
    getAvailability,
    state,
    availabilityArgs,
    availability,
    isFetchingAvailability,
    availabilityError,
  ]);

  useEffect(() => {
    if (availability?.canSchedule) {
      dispatch({ type: 'setErrors', payload: { _: undefined } });
    }
    if (availability?.canSchedule === false) {
      dispatch({ type: 'setErrors', payload: { _: availability.message } });
    }
    const error = confirmationError || availabilityError;
    if (error) {
      dispatch({ type: 'setErrors', payload: apiErrorsToDict(error) });
    }
    if (confirmationResponse?.canSchedule === false) {
      dispatch({ type: 'setErrors', payload: { _: confirmationResponse?.message } });
    }
  }, [confirmationResponse, confirmationError, availabilityError, availability, dispatch]);

  useEffect(() => {
    const { formData } = state;
    if (formData && formData?.startTime && formData?.finishTime) {
      if (
        formData.date &&
        formData.studentId &&
        availability &&
        availability &&
        availability.canSchedule
      ) {
        const body = {
          date: formData.date,
          finishTime: formData.finishTime,
          startTime: formData.startTime,
          studentId: formData.studentId,
        };
        if (!equals(body, confirmationRequest)) {
          confirmSelectedAvailability(body);
        }
      }
    }
  }, [availability, state, confirmationRequest, confirmSelectedAvailability]);

  const validate = useCallback(
    ({ validateGoals }: { validateGoals?: boolean } = {}) => {
      const tmpErrors: Record<string, string> = {};

      if (!state.formData?.studentId) {
        tmpErrors.studentId = 'Student has to be selected';
      }
      if (!state.formData?.startTime) {
        tmpErrors.startTime = 'Start time is missing';
      }
      if (!state.formData?.finishTime) {
        tmpErrors.finishTime = 'End time is missing';
      }
      if (!state.formData?.location) {
        tmpErrors.location = 'This field is required';
      }

      if (validateGoals && !isPara && state.formData) {
        if (hasSeitServiceType) {
          const { startTime, finishTime, goals = [] } = state.formData;
          if (!startTime || !finishTime) {
            tmpErrors.goals =
              'Start and End time are required to determine session duration for goals.';
          } else {
            const requiredGoalsCount =
              confirmationResponse?.canSchedule && confirmationResponse.requiredGoals !== undefined
                ? confirmationResponse.requiredGoals
                : (() => {
                    const baseDate = new Date(1970, 0, 1);
                    const start = parse(startTime, 'HH:mm', baseDate);
                    const finish = parse(finishTime, 'HH:mm', baseDate);
                    const durationInMinutes = differenceInMinutes(finish, start);
                    return Math.ceil(durationInMinutes / 30);
                  })();

            if (goals.length < requiredGoalsCount) {
              const baseDate = new Date(1970, 0, 1);
              const start = parse(startTime, 'HH:mm', baseDate);
              const finish = parse(finishTime, 'HH:mm', baseDate);
              const durationInMinutes = differenceInMinutes(finish, start);
              tmpErrors.goals = `At least ${requiredGoalsCount} goal(s) required for a ${durationInMinutes} min session.`;
            }

            const hasInvalidSeitGoals = goals.some(
              (goal) =>
                goal.type === 'seit' &&
                (!goal.activity?.length || !goal.participation || !goal.outcome),
            );

            if (hasInvalidSeitGoals) {
              tmpErrors.goals = 'All fields in SEIT goals must be filled.';
            }

            const hasInvalidSetssGoals = goals.some(
              (goal) => goal.type === 'setss' && goal.progress === undefined,
            );

            if (hasInvalidSetssGoals) {
              tmpErrors.goals = 'Progress must be set for all SETSS goals.';
            }
          }
        } else {
          // Validation for SETSS goals
          const setssGoals = state.formData.goals.filter(
            (g) => g.type === 'setss',
          ) as import('./RecordSessionContext').StudentGoalBase[];
          if (!setssGoals.length) {
            tmpErrors.goals = `Goals' must not be empty`;
          }
          if (setssGoals.find((goal) => goal.progress === 0)) {
            tmpErrors.goals = `Goals' must have rating`;
          }
        }
      }

      if (Object.keys(tmpErrors).length) {
        dispatch({ type: 'setErrors', payload: tmpErrors });
        return true;
      }

      return false;
    },
    [state.formData, isPara, dispatch, hasSeitServiceType, confirmationResponse],
  );

  const confirmSession = useCallback(
    ({ isReschedule }: { isReschedule?: boolean }) => {
      resetAllMutations();
      dispatch({ type: 'setErrors', payload: undefined });
      if (!('formData' in state) || !state.isInitialized) {
        return;
      }

      const hasErrors = validate({ validateGoals: !isReschedule });
      if (hasErrors) return;

      let editingId: string | undefined;

      const { goals, date, finishTime, startTime, location, note, studentId, numberOfStudents } =
        state.formData;

      if (!goals || !date || !finishTime || !startTime || !location || !studentId) {
        return;
      }

      if (state.isInitialized && 'existingSession' in state) {
        editingId = state.existingSession?.id;
      }

      const finalNumberOfStudents =
        numberOfStudents ?? (selectedStudent?.groupSize ? 1 : undefined);

      const baseData = {
        date,
        startTime,
        finishTime,
        location,
        note,
        goals: goals.map((goal) => {
          if (goal.type === 'setss') {
            return {
              id: goal.id,
              progress: goal.progress || 0,
            };
          } else {
            return {
              id: goal.id,
              activity: goal.activity || [],
              participation: goal.participation || '',
              outcome: goal.outcome || '',
            };
          }
        }),
        ...(finalNumberOfStudents !== undefined ? { numberOfStudents: finalNumberOfStudents } : {}),
      };

      if (isReschedule) {
        rescheduleRecordSession({ ...baseData, id: editingId! });
        return;
      }

      editingId
        ? confirmRecordSession({ ...baseData, id: editingId })
        : createRecordSession({ ...baseData, studentId });
    },
    [
      createRecordSession,
      confirmRecordSession,
      rescheduleRecordSession,
      resetAllMutations,
      state,
      validate,
      dispatch,
      selectedStudent?.groupSize,
    ],
  );

  const rescheduleSession = useCallback(() => {
    confirmSession({ isReschedule: true });
  }, [confirmSession]);

  const deleteSession = useCallback(() => {
    resetAllMutations();
    if (state.isInitialized && 'existingSession' in state && state.existingSession) {
      deleteRecordSession(state.existingSession.id);
    }
  }, [state, deleteRecordSession, resetAllMutations]);

  const cancelSession = useCallback(() => {
    resetAllMutations();
    if (
      'formData' in state &&
      'existingSession' in state &&
      state.existingSession?.id &&
      state.formData.cancellationReasonId
    ) {
      const data = {
        cancellationReason: state.formData.cancellationReason,
        cancellationReasonId: state.formData.cancellationReasonId,
        replacementSession: '',
        id: state.existingSession.id,
      };
      cancelRecordSession(data);
    }
    return state;
  }, [state, cancelRecordSession, resetAllMutations]);

  useEffect(() => {
    const handleIfError = (err) => {
      if (!err) return;
      const is400 = 'status' in err && err.status === 400;
      if (is400) {
        const errorsDict = apiErrorsToDict(err);
        dispatch({ type: 'setErrors', payload: errorsDict });
      } else {
        appDispatch(
          addToast({
            type: 'negative',
            text: 'Something went wrong. Try again later.',
          }),
        );
      }
    };
    handleIfError(confirmError);
    handleIfError(createError);
    handleIfError(deleteError);
    handleIfError(cancelError);
    handleIfError(rescheduleError);
  }, [confirmError, createError, deleteError, cancelError, rescheduleError, dispatch, appDispatch]);

  const formData = (state.isInitialized && state.formData) || ({} as FormData);
  const isLoading = 'isLoading' in state && (state as EditionForm).isLoading;
  const formOptions = ('options' in state && state.options) || ({} as FormOptions);
  const errors = state.isInitialized ? state.errors : undefined;

  return {
    data: {
      state,
      formData,
      isLoading,
      errors,
      isLoadingCreate,
      isLoadingConfirm,
      isLoadingReschedule,
      availability: state.formData?.studentId && state.formData?.date ? availability : undefined,
      options: formOptions,
      requiredGoals,
    },
    dispatch,
    actions: { confirmSession, cancelSession, deleteSession, rescheduleSession },
    isEdit,
    features: { isStudentLocked: !!features.lockOnStudentId },
  };
};
